import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAccount, useConfig } from "wagmi";
import { getPublicClient, getWalletClient, switchChain, signMessage } from "wagmi/actions";
import type { Address } from "viem";
import { keelReferidosAbi } from "../abi";
import type { RedKeel } from "../config/networks";
import { leeReferido, olvidaReferido } from "../lib/referido";

/**
 * Referidos: el alta, lo que ve quien invita y lo que ve quien fue invitado.
 *
 * DOS FUENTES, Y CADA UNA MANDA EN LO SUYO:
 *
 *   · LA CADENA manda en el dinero. `KeelReferidos` dice quien invito a quien y si ya cobra, y el
 *     reparto lo hace el vault al cobrar comisiones. Nada de lo que pase en el servidor mueve un
 *     centimo.
 *   · EL SERVICIO junta la cuenta, pero lo que enseña lo LEE DE LA CADENA: por cada invitado y red,
 *     `referidorDe` / `activo` / `generado` frente a `minGenerado` del registro, y lo pagado sale de
 *     los eventos `ReferidoPagado`. Un alta firmada que no llegó a la cadena se ve como tal
 *     ("no registrada"), nunca como "pendiente".
 *
 * SI FALTA CUALQUIERA DE LAS DOS, LA PANTALLA SIGUE SIENDO HONESTA. Sin registro desplegado se dice
 * que el reparto todavia no esta activo en esa red; sin servicio, se pierden los numeros pero no el
 * alta. Lo que no se hace nunca es enseñar un cero como si fuera un dato.
 */

const BASE = import.meta.env.VITE_REFERIDOS_URL ?? "/api/referidos";

async function pedirJson(ruta: string, cuerpo?: unknown) {
  const r = await fetch(BASE + ruta, {
    method: cuerpo === undefined ? "GET" : "POST",
    headers: cuerpo === undefined ? undefined : { "Content-Type": "application/json" },
    body: cuerpo === undefined ? undefined : JSON.stringify(cuerpo),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error ?? `el servidor de referidos respondio ${r.status}`);
  return d;
}

/** Estado HONESTO de una wallet en el registro de una red, leído de la cadena por el servicio. */
export type FichaRed = {
  estado: "sin_leer" | "no_registrada" | "otro_referidor" | "acumulando" | "activa";
  referidorEnCadena?: string | null;
  activo?: boolean;
  /** `generado(dueño, tokenRef)` del registro: comisiones BRUTAS en el token de referencia */
  generado?: Record<string, string>;
  /** `minGenerado(tokenRef)`: lo que tiene que generar para activarse */
  minimo?: Record<string, string | null>;
  estable: string;
  leido?: number;
  error?: string;
};

/** en_cadena | subiendo | parcial_caducada | caducada_sin_subir | otro_referidor */
export type EstadoAlta = "en_cadena" | "subiendo" | "parcial_caducada" | "caducada_sin_subir" | "otro_referidor";

export type EstadoReferido = {
  /** quien me invito, segun la cadena (cualquier red); null si nadie */
  referidorEnCadena: Address | null;
  /** quien me invito, segun el servicio (el alta firmada que guarda; puede NO estar en cadena) */
  referidorEnServicio: Address | null;
  /** ya genere lo suficiente para que mi referidor cobre */
  activo: boolean;
  /** el registro de esta red esta desplegado */
  hayRegistro: boolean;
  /** un enlace pendiente de aceptar, guardado del `?ref=` */
  pendiente: Address | null;
  /** estado del alta guardada en el servicio (si la hay) */
  estadoAlta: EstadoAlta | null;
  /** el alta guardada caducó sin llegar a alguna red: hay que volver a firmar */
  puedeRefirmar: boolean;
  /** redes (clave) donde NO estoy registrado en cadena */
  faltan: string[];
  /** ficha por red leida de la cadena */
  redes: Record<string, FichaRed>;
  /** en ESTA red (la de la wallet) ya estoy registrado en cadena */
  registradoAqui: boolean;
};

export function useMiReferido(red: RedKeel | undefined) {
  const { address } = useAccount();
  const config = useConfig();

  return useQuery({
    queryKey: ["referido", red?.clave, address],
    enabled: !!address,
    // Mientras el servicio sube un alta se mira cada 10 s: la firma vale 15 min y el usuario tiene
    // que ver "registrada" en cuanto lo esté, no dentro de dos minutos.
    refetchInterval: (q) => (q.state.data?.estadoAlta === "subiendo" ? 10_000 : 120_000),
    queryFn: async (): Promise<EstadoReferido> => {
      const registro = red?.referidos;
      let referidorEnCadena: Address | null = null;
      let activo = false;

      if (registro && red) {
        const cliente = getPublicClient(config, { chainId: red.idCadena })!;
        const c = { address: registro, abi: keelReferidosAbi } as const;
        const [ref, act] = await cliente.multicall({
          allowFailure: false,
          contracts: [
            { ...c, functionName: "referidorDe", args: [address!] },
            { ...c, functionName: "activo", args: [address!] },
          ],
        });
        const r = ref as Address;
        referidorEnCadena = r === "0x0000000000000000000000000000000000000000" ? null : r;
        activo = act as boolean;
      }

      // El servicio puede fallar (no desplegado, caido, sin nginx en `npm run dev`) y eso NO puede
      // vaciar lo que ya sabemos por la cadena.
      const delServicio = await pedirJson(`/estado?dueno=${address}`).catch(() => ({}));

      const pendiente = leeReferido();
      const redes = (delServicio.redes ?? {}) as Record<string, FichaRed>;
      return {
        referidorEnCadena: referidorEnCadena ?? ((delServicio.referidorEnCadena as Address) || null),
        referidorEnServicio: (delServicio.referidor as Address) ?? null,
        activo: activo || !!delServicio.activo,
        hayRegistro: !!registro,
        estadoAlta: (delServicio.estadoAlta as EstadoAlta) ?? null,
        puedeRefirmar: !!delServicio.puedeRefirmar,
        faltan: (delServicio.faltan as string[]) ?? [],
        redes,
        registradoAqui: !!referidorEnCadena,
        // Un enlace que apunta a uno mismo no es una invitacion: es alguien probando su propio
        // enlace. Se ignora aqui para no ofrecerle aceptar algo que el contrato va a rechazar.
        pendiente:
          pendiente && address && pendiente.toLowerCase() !== address.toLowerCase()
            ? pendiente
            : null,
      };
    },
  });
}

export type InvitadoPanel = {
  wallet: Address;
  /** estado del alta firmada en el servicio; null si solo existe en cadena (registrarPropio) */
  estadoAlta: EstadoAlta | null;
  /** registrada en cadena con ESTE referidor en al menos una red */
  registrada: boolean;
  /** activa (ya te paga) en al menos una red */
  activo: boolean;
  redes: Record<string, FichaRed>;
  /** lo que este invitado ya te ha pagado (eventos ReferidoPagado), red -> token -> wei */
  pagado: Record<string, Record<string, string>>;
};

export type PanelReferidor = {
  invitados: number;
  registrados?: number;
  activos: number;
  /** comisiones BRUTAS (registro `generado`) de los invitados registrados, red -> token -> unidades */
  generado: Record<string, Record<string, string>>;
  /** lo que de verdad llegó a tu wallet (ReferidoPagado), red -> token -> unidades */
  pagado: Record<string, Record<string, string>>;
  porInvitado?: InvitadoPanel[];
  /** "red:token" -> símbolo y decimales */
  tokens?: Record<string, { simbolo: string; decimales: number }>;
  redes?: string[];
};

/** Lo que ve quien invita. Sale entero del servicio: son agregados, no dinero. */
export function usePanelReferidor() {
  const { address } = useAccount();
  return useQuery({
    queryKey: ["panel-referidor", address],
    enabled: !!address,
    retry: false,
    refetchInterval: 120_000,
    queryFn: async (): Promise<PanelReferidor> => pedirJson(`/panel?referidor=${address}`),
  });
}

/**
 * Aceptar una invitacion. Dos caminos, y el bueno depende de lo que haya desplegado:
 *
 *   · CON SERVICIO (lo normal): se firma un texto y ya esta. No cuesta gas, no hay que esperar a un
 *     bloque, y el alta la sube a la cadena el keeper. En HyperEVM, con un bloque por minuto, esto
 *     es la diferencia entre aceptar una invitacion y abandonar a medias.
 *   · SIN SERVICIO: se llama al registro desde la wallet. Cuesta gas y hay que esperar, pero
 *     funciona sin depender de nada nuestro — y eso importa: si un dia el servicio no esta, el
 *     referido no deja de existir.
 */
export function useAceptarInvitacion(red: RedKeel | undefined) {
  const { address } = useAccount();
  const config = useConfig();
  const qc = useQueryClient();
  const [haciendo, setHaciendo] = useState<null | "firma" | "tx">(null);
  const [error, setError] = useState<string | null>(null);
  const [hecho, setHecho] = useState(false);

  async function refrescar() {
    await qc.invalidateQueries({ queryKey: ["referido"] });
  }

  async function conFirma(referidor: Address) {
    // El alta es una FIRMA sin gas (personal_sign), INDEPENDIENTE de la cadena: el backend recupera
    // al firmante esté en la red que esté. Se usa la acción `signMessage` de wagmi (no
    // `getWalletClient`) a propósito: pedir el wallet client obliga a una cadena concreta y revienta
    // —o ni abre el diálogo de firma— cuando la wallet está en una red que la config no conoce
    // (p. ej. Ethereum). `signMessage` firma con el conector tal cual, sin exigir cadena.
    if (!address) throw new Error("conecta la wallet");
    const { mensaje, caduca } = await pedirJson("/nonce", { dueno: address, referidor });
    const firma = await signMessage(config, { account: address, message: mensaje });
    await pedirJson("/alta", { dueno: address, referidor, caduca, firma });
  }

  async function conTransaccion(referidor: Address) {
    if (!red?.referidos) throw new Error("esta red todavia no tiene registro de referidos");
    // Esto SÍ es una transacción on-chain: hay que estar en la red del registro. Si la wallet está
    // en otra, se cambia antes (si no, wagmi da el mismo desajuste de cadena).
    await switchChain(config, { chainId: red.idCadena }).catch(() => {});
    const wallet = await getWalletClient(config, { chainId: red.idCadena });
    const cliente = getPublicClient(config, { chainId: red.idCadena })!;
    if (!wallet || !address) throw new Error("conecta la wallet");
    const hash = await wallet.writeContract({
      address: red.referidos,
      abi: keelReferidosAbi,
      functionName: "registrarPropio",
      args: [referidor],
      account: address,
      chain: null,
    });
    await cliente.waitForTransactionReceipt({ hash });
  }

  /** Un camino concreto, sin caer al otro: lo eligió el usuario en el aviso de re-firma. */
  async function solo(como: "firma" | "tx", referidor: Address) {
    setError(null);
    try {
      setHaciendo(como);
      if (como === "firma") await conFirma(referidor);
      else await conTransaccion(referidor);
    } catch (e) {
      const m = e instanceof Error ? e.message : String(e);
      setError(/rejected|denied|User rejected/i.test(m) ? null : m);
      setHaciendo(null);
      return;
    }
    olvidaReferido();
    setHecho(true);
    setHaciendo(null);
    await refrescar();
  }

  return {
    haciendo,
    error,
    hecho,
    limpiar: () => { setError(null); setHecho(false); },
    /** Firma nueva (sin gas): el servicio la sube a todas las redes mientras vale. */
    refirmar: (referidor: Address) => solo("firma", referidor),
    /** `registrarPropio(referidor)` en la red actual: lo paga el usuario, no depende del servicio. */
    registrarYo: (referidor: Address) => solo("tx", referidor),

    aceptar: async (referidor: Address) => {
      setError(null);
      try {
        setHaciendo("firma");
        await conFirma(referidor);
      } catch (e) {
        const m = e instanceof Error ? e.message : String(e);
        // Un rechazo en la wallet es alguien diciendo que no, no un fallo que haya que gritar — y no
        // tiene sentido reintentarlo por otro camino.
        if (/rejected|denied|User rejected/i.test(m)) {
          setHaciendo(null);
          return;
        }
        // El servicio no esta: se intenta por la cadena antes de darse por vencido.
        try {
          setHaciendo("tx");
          await conTransaccion(referidor);
        } catch (e2) {
          const m2 = e2 instanceof Error ? e2.message : String(e2);
          setError(/rejected|denied|User rejected/i.test(m2) ? null : m2);
          setHaciendo(null);
          return;
        }
      }
      // Guardado ya del lado bueno: el `?ref=` local ha cumplido y no tiene que sobrevivir a nada.
      olvidaReferido();
      setHecho(true);
      setHaciendo(null);
      await refrescar();
    },

    rechazar: async () => {
      olvidaReferido();
      await refrescar();
    },
  };
}
